import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '../../../Helpers/logger';

// Simple debounce utility
function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

export function useBidirectionalInfiniteScroll({
    containerRef,
    onLoadTop = null,
    onLoadBottom = null,
    hasMoreTop = false,
    hasMoreBottom = false,
    offset = 50,
    debounceMs = 300,
    isReversed = false, // 🔹 keep for data logic, not scroll math
}) {
    const [loadingTop, setLoadingTop] = useState(false);
    const [loadingBottom, setLoadingBottom] = useState(false);

    const loadingTopRef = useRef(false);
    const loadingBottomRef = useRef(false);
    const lastTopTriggerRef = useRef(0);
    const lastBottomTriggerRef = useRef(0);
    const COOLDOWN_MS = 300;

    // Keep refs in sync without re-rendering unnecessarily
    useEffect(() => {
        loadingTopRef.current = loadingTop;
    }, [loadingTop]);
    useEffect(() => {
        loadingBottomRef.current = loadingBottom;
    }, [loadingBottom]);

    useEffect(() => {
        if (!hasMoreBottom && loadingBottomRef.current) {
            loadingBottomRef.current = false;
            setLoadingBottom(false);
        }
    }, [hasMoreBottom]);

    const handleScroll = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;

        if (el.scrollHeight <= el.clientHeight) return;

        const allowTop = hasMoreTop && typeof onLoadTop === 'function';
        const allowBottom = hasMoreBottom && typeof onLoadBottom === 'function';

        if (!allowTop && !allowBottom) return;

        // Handle negative scrollTop values that occur with flex-direction: column-reverse
        const rawScrollTop = el.scrollTop;
        const scrollHeight = el.scrollHeight;
        const clientHeight = el.clientHeight;

        // Calculate distances from top and bottom using absolute values
        let distanceFromTop, distanceFromBottom;

        if (rawScrollTop >= 0) {
            // Normal case: positive scrollTop
            distanceFromTop = rawScrollTop;
            distanceFromBottom = scrollHeight - rawScrollTop - clientHeight;
        } else {
            // Reversed case: negative scrollTop
            // In column-reverse, negative scrollTop means we're scrolled down
            distanceFromTop = Math.abs(rawScrollTop);
            distanceFromBottom = scrollHeight + rawScrollTop - clientHeight;
        }

        // Ensure we don't get negative distances
        distanceFromTop = Math.max(0, distanceFromTop);
        distanceFromBottom = Math.max(0, distanceFromBottom);

        // 🔹 TOP trigger - check if we're near the top
        const topTrigger = allowTop && distanceFromTop <= offset;

        if (topTrigger && !loadingTopRef.current) {
            const now = Date.now();
            if (now - lastTopTriggerRef.current >= COOLDOWN_MS) {
                lastTopTriggerRef.current = now;
                loadingTopRef.current = true;
                setLoadingTop(true);
                Promise.resolve(onLoadTop?.())
                    .catch(error => {
                        logger.error('❌ onLoadTop failed:', error);
                    })
                    .finally(() => {
                        setLoadingTop(false);
                        loadingTopRef.current = false;
                    });
            }
            return;
        }

        // 🔹 BOTTOM trigger - check if we're near the bottom
        const bottomTrigger = allowBottom && distanceFromBottom <= offset;

        if (bottomTrigger && !loadingBottomRef.current) {
            const now = Date.now();
            if (now - lastBottomTriggerRef.current >= COOLDOWN_MS) {
                lastBottomTriggerRef.current = now;
                loadingBottomRef.current = true;
                setLoadingBottom(true);

                setTimeout(() => {
                    Promise.resolve(onLoadBottom?.())
                        .catch(error => {
                            logger.error('❌ onLoadBottom failed:', error);
                        })
                        .finally(() => {
                            setLoadingBottom(false);
                            loadingBottomRef.current = false;
                        });
                }, 100); // 100ms delay to allow layout to settle
            }
        }
    }, [containerRef, onLoadTop, onLoadBottom, hasMoreTop, hasMoreBottom, offset]);

    // Attach debounced scroll listener
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const debounced = debounce(handleScroll, debounceMs);
        el.addEventListener('scroll', debounced);
        // Trigger once on mount to catch already-scrolled containers
        handleScroll();

        return () => {
            el.removeEventListener('scroll', debounced);
        };
    }, [handleScroll, debounceMs, containerRef]);

    return { loadingTop, loadingBottom };
}
